import { api } from "@/lib/api";

const DRAFT_KEY = "alaine_compass_drafts_v1";
const DRAFT_META_KEY = "alaine_compass_draft_meta_v1";
const DRAFT_OWNER_KEY = "alaine_compass_draft_owner_v1";
const DRAFT_SYNCED_KEY = "alaine_compass_draft_synced_v1";

export function loadDrafts() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}"); } catch { return {}; }
}

export function loadDraftMeta() {
  try { return JSON.parse(localStorage.getItem(DRAFT_META_KEY) || "{}"); } catch { return {}; }
}

export function loadDraftSynced() {
  try { return JSON.parse(localStorage.getItem(DRAFT_SYNCED_KEY) || "{}"); } catch { return {}; }
}

export function markDraftSynced(templateId, at) {
  try {
    const synced = loadDraftSynced();
    synced[templateId] = at;
    localStorage.setItem(DRAFT_SYNCED_KEY, JSON.stringify(synced));
  } catch { /* best-effort */ }
}

export function loadDraftOwner() {
  try { return localStorage.getItem(DRAFT_OWNER_KEY) || null; } catch { return null; }
}

export function saveDraftsLocally(drafts, meta, owner) {
  try {
    // A shared browser may still contain another founder's cache while auth is
    // changing. Normal saves must never claim or overwrite that cache.
    const currentOwner = localStorage.getItem(DRAFT_OWNER_KEY) || null;
    if (owner !== undefined && currentOwner && owner && currentOwner !== owner) {
      return { ok: false, reason: "owner-mismatch" };
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));
    localStorage.setItem(DRAFT_META_KEY, JSON.stringify(meta));
    if (owner !== undefined) {
      if (owner) localStorage.setItem(DRAFT_OWNER_KEY, owner);
      else localStorage.removeItem(DRAFT_OWNER_KEY);
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: "storage-unavailable" };
  }
}

// Server merge is the one operation allowed to replace a previous owner's
// cache: it has deliberately sent an empty payload for that previous owner and
// received only the currently authenticated user's data.
function replaceDraftsLocally(drafts, meta, owner) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));
    localStorage.setItem(DRAFT_META_KEY, JSON.stringify(meta));
    localStorage.setItem(DRAFT_OWNER_KEY, owner);
    return { ok: true };
  } catch {
    return { ok: false, reason: "storage-unavailable" };
  }
}

export function canSyncDrafts(user) {
  return !!user && !!user.id && user.role !== "demo";
}

export async function mergeDraftsWithServer(user) {
  if (!canSyncDrafts(user)) return;
  const owner = loadDraftOwner();
  const ownedByUser = owner === null || owner === user.id;
  const drafts = ownedByUser ? loadDrafts() : {};
  const meta = ownedByUser ? loadDraftMeta() : {};
  if (!ownedByUser) {
    try { localStorage.removeItem(DRAFT_SYNCED_KEY); } catch { /* ignore */ }
  }
  const payload = {};
  Object.keys(drafts).forEach((tid) => {
    payload[tid] = { values: drafts[tid], updated_at: meta[tid] || null };
  });
  const { data } = await api.post("/compass/drafts/merge", { drafts: payload }, { skipNetToast: true });
  const merged = data?.drafts || {};
  const nextDrafts = {};
  const nextMeta = {};
  Object.keys(merged).forEach((tid) => {
    nextDrafts[tid] = merged[tid].values || {};
    if (merged[tid].updated_at) nextMeta[tid] = merged[tid].updated_at;
  });
  const saved = replaceDraftsLocally(nextDrafts, nextMeta, user.id);
  if (!saved.ok) throw new Error("Compass drafts synced, but browser storage is unavailable.");
  const syncedAt = new Date().toISOString();
  Object.keys(merged).forEach((tid) => markDraftSynced(tid, syncedAt));
  
  // Dispatch a custom event so active hooks can hydrate from the new merged state.
  window.dispatchEvent(new CustomEvent("compass-drafts-merged"));
}

export function hasPendingDraftSync() {
  const meta = loadDraftMeta();
  const synced = loadDraftSynced();
  return Object.keys(meta).some((tid) => !synced[tid] || synced[tid] < meta[tid]);
}
